import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { paymentAPI } from '../services/api';
import { FiAlertCircle, FiRefreshCw, FiCalendar, FiDollarSign, FiMail, FiPhone } from 'react-icons/fi';

const FailedPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: Math.floor(Date.now() / 1000) - (30 * 24 * 60 * 60), // Last 30 days
    to: Math.floor(Date.now() / 1000)
  });
  const [stats, setStats] = useState({
    total: 0,
    totalAmount: 0
  });

  useEffect(() => {
    fetchFailedPayments();
  }, []);

  const fetchFailedPayments = async () => {
    setLoading(true);
    try {
      const response = await paymentAPI.getFailedPayments({
        from: dateRange.from,
        to: dateRange.to,
        count: 100
      });
      
      if (response.data.success) {
        setPayments(response.data.data);
        
        // Calculate stats
        const totalAmount = response.data.data.reduce((sum, p) => sum + parseFloat(p.amount), 0);
        setStats({
          total: response.data.total,
          totalAmount: totalAmount
        });
      } else {
        toast.error(response.data.message || 'Failed to fetch payments');
      }
    } catch (error) {
      toast.error('Error fetching failed payments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (days) => {
    const to = Math.floor(Date.now() / 1000);
    const from = to - (days * 24 * 60 * 60);
    setDateRange({ from, to });
  };

  const handleRefresh = () => {
    fetchFailedPayments();
  };

  const getErrorBadgeColor = (errorCode) => {
    if (!errorCode) return 'bg-gray-100 text-gray-800';
    if (errorCode.includes('GATEWAY')) return 'bg-red-100 text-red-800';
    if (errorCode.includes('CUSTOMER')) return 'bg-yellow-100 text-yellow-800';
    if (errorCode.includes('BUSINESS')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Failed Payments</h1>
          <p className="text-gray-600 mt-1">Monitor and track failed payment transactions</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <FiAlertCircle className="text-2xl text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Failed Payments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FiDollarSign className="text-2xl text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Amount Lost</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FiCalendar className="text-gray-600" />
          <span className="text-sm text-gray-600 font-medium">Quick Filter:</span>
          <button
            onClick={() => { handleDateRangeChange(7); fetchFailedPayments(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(30); fetchFailedPayments(); }}
            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg"
          >
            Last 30 Days
          </button>
          <button
            onClick={() => { handleDateRangeChange(90); fetchFailedPayments(); }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Last 90 Days
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12">
            <FiAlertCircle className="mx-auto text-5xl text-gray-400 mb-4" />
            <p className="text-gray-600">No failed payments found in the selected date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{payment.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {payment.email && (
                          <div className="flex items-center gap-1 text-gray-900">
                            <FiMail className="text-gray-400" />
                            {payment.email}
                          </div>
                        )}
                        {payment.contact && (
                          <div className="flex items-center gap-1 text-gray-600 mt-1">
                            <FiPhone className="text-gray-400" />
                            {payment.contact}
                          </div>
                        )}
                        {!payment.email && !payment.contact && (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ₹{parseFloat(payment.amount).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">{payment.currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                        {payment.method || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {payment.error_code && (
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getErrorBadgeColor(payment.error_code)}`}>
                            {payment.error_code}
                          </span>
                        )}
                        {payment.error_description && (
                          <div className="text-xs text-gray-600 max-w-xs">
                            {payment.error_description}
                          </div>
                        )}
                        {payment.error_reason && (
                          <div className="text-xs text-gray-500">
                            Reason: {payment.error_reason}
                          </div>
                        )}
                        {!payment.error_code && !payment.error_description && (
                          <span className="text-xs text-gray-400">No error details</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(payment.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FailedPayments;
